import React, { useEffect, useRef } from 'react';

const COLORS = [
  '#ff85a2', // Pink
  '#b388eb', // Purple
  '#f7d08a', // Yellow
  '#ff4d6d', // Red
  '#99e2b4', // Green
  '#ff9e00', // Orange
  '#4cc9f0', // Sky Blue
  '#f15bb5', // Hot Pink
];

interface Bean {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  color: string;
  angle: number;
  vAngle: number;
  opacity: number;
}

export const JellyBeanBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const beansRef = useRef<Bean[]>([]);
  const requestRef = useRef<number>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', resize);
    resize();

    // Initialize beans
    const beanCount = 35;
    const beans: Bean[] = [];
    for (let i = 0; i < beanCount; i++) {
      const radius = 12 + Math.random() * 15;
      beans.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 1.5,
        vy: (Math.random() - 0.5) * 1.5,
        radius,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        angle: Math.random() * Math.PI * 2,
        vAngle: (Math.random() - 0.5) * 0.02,
        opacity: 0.3 + Math.random() * 0.3,
      });
    }
    beansRef.current = beans;

    const update = () => {
      const beans = beansRef.current;
      const w = canvas.width;
      const h = canvas.height;

      for (let i = 0; i < beans.length; i++) {
        const b = beans[i];
        
        // Update position
        b.x += b.vx;
        b.y += b.vy;
        b.angle += b.vAngle;

        // Wall collisions
        if (b.x - b.radius < 0) { b.x = b.radius; b.vx *= -1; }
        if (b.x + b.radius > w) { b.x = w - b.radius; b.vx *= -1; }
        if (b.y - b.radius < 0) { b.y = b.radius; b.vy *= -1; }
        if (b.y + b.radius > h) { b.y = h - b.radius; b.vy *= -1; }

        // Bean to bean collisions
        for (let j = i + 1; j < beans.length; j++) {
          const b2 = beans[j];
          const dx = b2.x - b.x;
          const dy = b2.y - b.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const minDistance = b.radius + b2.radius;

          if (distance < minDistance) {
            // Collision detected - Resolve overlap
            const overlap = minDistance - distance;
            const nx = dx / distance; // Normal X
            const ny = dy / distance; // Normal Y
            
            // Push them apart a bit
            b.x -= nx * overlap / 2;
            b.y -= ny * overlap / 2;
            b2.x += nx * overlap / 2;
            b2.y += ny * overlap / 2;

            // Elastic collision math
            // Dot product of velocity and normal
            const v1n = b.vx * nx + b.vy * ny;
            const v2n = b2.vx * nx + b2.vy * ny;

            // Exchange velocities along the normal (assuming equal mass for simplicity, 
            // or we could use radius squared for mass)
            const m1 = b.radius * b.radius;
            const m2 = b2.radius * b2.radius;
            
            const p = (2 * (v1n - v2n)) / (m1 + m2);

            b.vx -= p * m2 * nx;
            b.vy -= p * m2 * ny;
            b2.vx += p * m1 * nx;
            b2.vy += p * m1 * ny;
            
            // Add a little 2D spin nudge on collision
            b.vAngle += (Math.random() - 0.5) * 0.01;
            b2.vAngle += (Math.random() - 0.5) * 0.01;
          }
        }
      }

      // Draw
      ctx.clearRect(0, 0, w, h);
      beans.forEach(b => {
        ctx.save();
        ctx.translate(b.x, b.y);
        ctx.rotate(b.angle);
        ctx.globalAlpha = b.opacity;
        
        // Shadow for depth
        ctx.shadowColor = 'rgba(0,0,0,0.1)';
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 4;
        ctx.shadowOffsetY = 4;

        // Draw the jelly bean (ellipse)
        ctx.beginPath();
        // Radii for the bean shape
        const rx = b.radius;
        const ry = b.radius * 1.5;
        ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
        
        // Simple 2D shading
        const grad = ctx.createRadialGradient(-rx * 0.3, -ry * 0.3, rx * 0.1, 0, 0, ry);
        grad.addColorStop(0, '#ffffff88');
        grad.addColorStop(0.3, b.color);
        grad.addColorStop(1, b.color);
        
        ctx.fillStyle = grad;
        ctx.fill();
        
        // Inner highlight
        ctx.beginPath();
        ctx.ellipse(-rx * 0.4, -ry * 0.4, rx * 0.2, ry * 0.2, Math.PI/4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.4)';
        ctx.fill();

        ctx.restore();
      });

      requestRef.current = requestAnimationFrame(update);
    };

    requestRef.current = requestAnimationFrame(update);

    return () => {
      window.removeEventListener('resize', resize);
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
    />
  );
};